// front-end/src/components/vacancy-manager/vacancy-manager.js

import {apiDelete, apiGet, apiPatch, apiPost, apiPut} from '../../utils/api';
import {getAuthToken} from '../auth';
import {showErrorNotification, showSuccessNotification} from '../../utils/notifications';
import {openLinkPageModal} from '../diagnostic-manager/link-page-modal';
import {config} from '../../config/app-config';

export class VacancyManager {
    constructor() {
        this.init();
    }

    init() {
        console.log('VacancyManager initialized');
        document.addEventListener('blockPublished', this.handleBlockPublished.bind(this));
        document.addEventListener('blockAdded', this.handleBlockAdded.bind(this));
        this.processExistingVacancyBlocks();
    }

    handleBlockAdded(event) {
        const { blockElement } = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');
        if (blockName && blockName.startsWith('vacancy-') && getAuthToken()) {
            this.addLinkButtonToBlock(blockElement);
        }
    }

    // Проверяем, должны ли показываться статусы
    shouldShowStatusControls() {
        return window.location.pathname.match(/vacancies(\.html)?$/);
    }

    handleBlockPublished(event) {
        const {blockElement, blockId, blockData} = event.detail;
        const blockName = blockElement.getAttribute('data-block-name');

        if (blockName && blockName.startsWith('vacancy-')) {
            console.log('Vacancy block published:', blockName);
            this.activateVacancyLink(blockElement, blockName);

            const pendingId = blockElement.getAttribute('data-pending-link-id');
            if (pendingId) {
                this._applyPendingLink(blockElement, blockId, pendingId);
            } else {
                this.createVacancyEntity(blockElement, blockId, blockData);
            }
        }
    }

    async _applyPendingLink(blockElement, blockId, entityId) {
        try {
            const result = await apiPatch(`${config.api.paths.vacanciesLinkBlock}/${entityId}/block`, { block_id: parseInt(blockId, 10) });
            if (result?.id) {
                blockElement.setAttribute('data-vacancy-id', result.id);
                blockElement.setAttribute('data-vacancy-status', result.status || 'draft');
                blockElement.setAttribute('data-vacancy-slug', result.slug);
                blockElement.removeAttribute('data-pending-link-id');
                blockElement.removeAttribute('data-pending-link-slug');
                this.activateVacancyLink(blockElement, result.slug);
            }
        } catch (err) {
            console.error('Error applying pending vacancy link:', err);
        }
    }

    activateVacancyLink(blockElement, blockName) {
        const vacancyLink = blockElement.querySelector('a.news-link-to-feed-container');
        if (vacancyLink) {
            const dynamicUrl = `/vacancy/${blockName}`;
            vacancyLink.href = dynamicUrl;
            vacancyLink.setAttribute('data-page', blockName);
            vacancyLink.classList.remove('inactive');
            vacancyLink.classList.add('active');

            console.log(`Vacancy link activated for ${blockName}: ${dynamicUrl}`);
        }
    }

    async createVacancyEntity(blockElement, blockId, blockData) {
        const token = getAuthToken();
        if (!token) return;

        try {
            const blockName = blockData.data_block_name;

            // Создаем slug для вакансии из имени блока
            const vacancySlug = blockName;

            const vacancyData = {
                title: blockName.replace('vacancy-', '').replace(/-/g, ' ').toUpperCase(),
                slug: vacancySlug,
                preview_text: 'Додайте опис вакансії через редактор контенту',
                page_title: `Vacancy - ${blockName}`,
                page_slug: `vacancy-${Date.now()}`,
                block_data_name: blockName  // Только имя блока
            };

            console.log('Creating vacancy entity with data:', vacancyData);

            let result = await apiPost('/api/vacancies', vacancyData);
            if (!result?.id) {
                const existing = await apiGet(`${config.api.paths.vacanciesBySlug}/${vacancySlug}`, { auth: false }).catch(() => null);
                if (existing?.id && blockId) {
                    result = await apiPatch(
                        `${config.api.paths.vacanciesLinkBlock}/${existing.id}/block`,
                        { block_id: parseInt(blockId, 10) }
                    ).catch(() => null);
                }
            }

            if (result && result.id) {
                // Update block element with vacancy info
                blockElement.setAttribute('data-vacancy-id', result.id);
                blockElement.setAttribute('data-vacancy-status', result.status || 'draft');
                blockElement.setAttribute('data-vacancy-slug', result.slug);

                // Add status indicator - ТОЛЬКО на vacancies.html
                if (this.shouldShowStatusControls()) {
                    this.updateBlockStatusIndicator(blockElement, result.status || 'draft');
                }

                // Add status controls for authenticated users - ТОЛЬКО на vacancies.html
                if (getAuthToken() && this.shouldShowStatusControls()) {
                    this.addStatusControlsToBlock(blockElement, result.id);
                }

                this.addLinkButtonToBlock(blockElement);
                console.log('Vacancy entity created successfully:', result);
                console.log(`Block ${blockName} now linked to vacancy ID: ${result.id}`);
                showSuccessNotification('Вакансія створена в статусі чернетки');
            }

        } catch (error) {
            console.error('Error creating vacancy entity:', error);
            showErrorNotification('Помилка створення вакансії');
        }
    }

    addStatusControlsToBlock(blockElement, vacancyId) {
        // НОВОЕ: Проверяем, должны ли показываться контролы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        // Удаляем существующие контролы если есть
        const existingControls = blockElement.querySelector('.vacancy-status-controls');
        if (existingControls) {
            existingControls.remove();
        }

        const currentStatus = blockElement.getAttribute('data-vacancy-status') || 'draft';

        const controlsContainer = document.createElement('div');
        controlsContainer.className = 'vacancy-status-controls';
        controlsContainer.innerHTML = `
            <div class="status-controls-inner">
                <span class="current-status">Статус: ${currentStatus.toUpperCase()}</span>
                <button class="toggle-status-btn" data-vacancy-id="${vacancyId}" data-current-status="${currentStatus}">
                    ${currentStatus === 'draft' ? 'Опублікувати' : 'Зняти з публікації'}
                </button>
            </div>
        `;

        blockElement.appendChild(controlsContainer);

        // Add event listener for status toggle
        const toggleBtn = controlsContainer.querySelector('.toggle-status-btn');
        toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleVacancyStatus(blockElement, vacancyId, currentStatus);
        });

        this._addSlugEditor(blockElement, vacancyId, 'vacancy');
        console.log(`Status controls added to block for vacancy ID: ${vacancyId}`);
    }

    async toggleVacancyStatus(blockElement, vacancyId, currentStatus) {
        const token = getAuthToken();
        if (!token) {
            showErrorNotification('Потрібна авторизація');
            return;
        }

        try {
            const newStatus = currentStatus === 'draft' ? 'published' : 'draft';

            console.log(`Toggling vacancy status from ${currentStatus} to ${newStatus}`);

            // Call the toggle publication endpoint
            const result = await apiPost(`/api/vacancies/${vacancyId}/publish`);

            if (result) {
                // Update block attributes
                blockElement.setAttribute('data-vacancy-status', result.status);

                // Update status indicator
                this.updateBlockStatusIndicator(blockElement, result.status);

                // Update controls
                this.updateStatusControls(blockElement, result.status);

                // Update link status
                const vacancyLink = blockElement.querySelector('a.news-link-to-feed-container');
                if (vacancyLink) {
                    if (result.status === 'published') {
                        vacancyLink.classList.remove('inactive');
                        vacancyLink.classList.add('active');
                        const vacancySlug = blockElement.getAttribute('data-vacancy-slug');
                        vacancyLink.href = `/vacancy/${vacancySlug}`;
                    } else {
                        vacancyLink.classList.remove('active');
                        vacancyLink.classList.add('inactive');
                        vacancyLink.href = '#';
                    }
                }

                const statusText = result.status === 'published' ? 'опублікована' : 'знята з публікації';
                showSuccessNotification(`Вакансія ${statusText} успішно`);

                console.log('Status updated successfully:', result);
            }

        } catch (error) {
            console.error('Error toggling vacancy status:', error);
            showErrorNotification('Помилка оновлення статусу вакансії');
        }
    }

    updateBlockStatusIndicator(blockElement, status) {
        // ФИКС: Проверяем авторизацию перед показом индикатора
        if (!getAuthToken()) {
            // Для неавторизованных удаляем индикатор
            const existingIndicator = blockElement.querySelector('.vacancy-status-indicator');
            if (existingIndicator) {
                existingIndicator.remove();
            }
            return;
        }

        // НОВОЕ: Проверяем, должны ли показываться индикаторы
        if (!this.shouldShowStatusControls()) {
            return;
        }

        let indicator = blockElement.querySelector('.vacancy-status-indicator');

        if (!indicator) {
            indicator = document.createElement('div');
            indicator.className = 'vacancy-status-indicator';
            blockElement.appendChild(indicator);
        }

        indicator.className = `vacancy-status-indicator status-${status}`;
        indicator.innerHTML = `<span class="status-text">${status.toUpperCase()}</span>`;
    }

    updateStatusControls(blockElement, status) {
        const controls = blockElement.querySelector('.vacancy-status-controls');
        if (controls) {
            const statusSpan = controls.querySelector('.current-status');
            const toggleBtn = controls.querySelector('.toggle-status-btn');

            if (statusSpan) {
                statusSpan.textContent = `Статус: ${status.toUpperCase()}`;
            }

            if (toggleBtn) {
                toggleBtn.textContent = status === 'draft' ? 'Опублікувати' : 'Зняти з публікації';
                toggleBtn.setAttribute('data-current-status', status);
            }
        }
    }

    addLinkButtonToBlock(blockElement) {
        if (!getAuthToken()) return;
        if (blockElement.querySelector('.vacancy-link-btn')) return;

        const btn = document.createElement('button');
        btn.className = 'vacancy-link-btn block-control-btn';
        btn.title = 'Прив\'язати сторінку';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
            <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
        </svg>`;
        Object.assign(btn.style, {
            position: 'absolute', top: '10px', right: '70px',
            background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '4px',
            color: 'white', cursor: 'pointer', padding: '4px', zIndex: '10',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const blockId = blockElement.getAttribute('data-block-id') || null;
            const currentSlug = blockElement.getAttribute('data-vacancy-slug') || blockElement.getAttribute('data-pending-link-slug') || null;
            openLinkPageModal(blockId, currentSlug, (result) => {
                if (blockId) {
                    if (result.slug) {
                        blockElement.setAttribute('data-vacancy-id', result.id);
                        blockElement.setAttribute('data-vacancy-slug', result.slug);
                        this.activateVacancyLink(blockElement, result.slug);
                    } else {
                        blockElement.removeAttribute('data-vacancy-slug');
                        this.activateVacancyLink(blockElement, null);
                    }
                } else {
                    blockElement.setAttribute('data-pending-link-id', result.id);
                    blockElement.setAttribute('data-pending-link-slug', result.slug);
                    btn.style.background = 'rgba(25,118,210,0.8)';
                }
            }, {
                listPath: `${config.api.paths.vacancies}?limit=200`,
                linkPath: config.api.paths.vacanciesLinkBlock,
            });
        });

        blockElement.appendChild(btn);
    }

    processExistingVacancyBlocks() {
        const vacancyBlocks = document.querySelectorAll('[data-block-name^="vacancy-"][data-block-id]');

        vacancyBlocks.forEach(blockElement => {
            const blockName = blockElement.getAttribute('data-block-name');
            const vacancyId = blockElement.getAttribute('data-vacancy-id');
            const vacancyStatus = blockElement.getAttribute('data-vacancy-status') || 'draft';
            const vacancySlug = blockElement.getAttribute('data-vacancy-slug');

            // Activate link for any published block
            if (vacancyStatus === 'published' && vacancySlug) {
                this.activateVacancyLink(blockElement, vacancySlug);
            }

            // ФИКС: Add status indicator - ТОЛЬКО для авторизованных на vacancies.html
            if (this.shouldShowStatusControls() && getAuthToken()) {
                this.updateBlockStatusIndicator(blockElement, vacancyStatus);
            } else {
                // Удаляем индикатор для неавторизованных
                const existingIndicator = blockElement.querySelector('.vacancy-status-indicator');
                if (existingIndicator) {
                    existingIndicator.remove();
                }
            }

            // Add status controls if vacancy entity exists and user is authenticated
            if (vacancyId && getAuthToken() && this.shouldShowStatusControls()) {
                this.addStatusControlsToBlock(blockElement, vacancyId);
            }

            this.addLinkButtonToBlock(blockElement);
        });
    }

    // Static method to handle vacancy block deletion - called by block system
    static async handleVacancyBlockDeletion(blockElement) {
        const vacancyId = blockElement.getAttribute('data-vacancy-id');
        const blockName = blockElement.getAttribute('data-block-name');

        if (!vacancyId) {
            console.log(`Vacancy block ${blockName} has no vacancy entity to delete`);
            return false;
        }

        if (!getAuthToken()) {
            console.error('Authentication required for vacancy deletion');
            return false;
        }

        try {
            console.log(`Deleting vacancy entity: ${vacancyId} for block: ${blockName}`);

            // Call the cascading delete endpoint
            await apiDelete(`/api/vacancies/${vacancyId}`);

            console.log(`Vacancy entity ${vacancyId} deleted successfully with cascade`);
            return true;

        } catch (error) {
            console.error('Error deleting vacancy entity:', error);
            return false;
        }
    }

    _toSlug(text) {
        const uk = {
            'а':'a','б':'b','в':'v','г':'h','ґ':'g','д':'d','е':'e','є':'ye',
            'ж':'zh','з':'z','и':'y','і':'i','ї':'yi','й':'y','к':'k','л':'l',
            'м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
            'ф':'f','х':'kh','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ь':'',
            'ю':'yu','я':'ya',
        };
        return text.toLowerCase()
            .split('').map(c => uk[c] ?? c).join('')
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    }

    _addSlugEditor(blockElement, entityId, type) {
        if (blockElement.querySelector('.slug-editor')) return;

        const currentSlug = blockElement.getAttribute(`data-${type}-slug`) || '';
        const apiPath = type === 'vacancy' ? `/api/vacancies/${entityId}` : `/api/news/${entityId}`;

        const wrap = document.createElement('div');
        wrap.className = 'slug-editor';
        Object.assign(wrap.style, {
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 6px', background: 'rgba(0,0,0,0.55)',
            borderRadius: '0 0 4px 4px', flexWrap: 'wrap',
        });
        wrap.innerHTML = `
            <span style="color:#aaa;font-size:11px;white-space:nowrap">slug:</span>
            <input class="slug-input" type="text" value="${currentSlug}"
                style="flex:1;min-width:80px;font-size:11px;padding:2px 4px;border-radius:3px;border:1px solid #555;background:#222;color:#fff;outline:none">
            <button class="slug-auto-btn" title="Авто з заголовку"
                style="font-size:10px;padding:2px 5px;background:#444;border:none;border-radius:3px;color:#ccc;cursor:pointer;white-space:nowrap">Auto</button>
            <button class="slug-save-btn"
                style="font-size:10px;padding:2px 6px;background:#1976d2;border:none;border-radius:3px;color:#fff;cursor:pointer;white-space:nowrap">Save</button>
            ${type === 'vacancy' ? `<button class="slug-recover-btn" title="Відновити контент з оригінального імені блоку"
                style="font-size:10px;padding:2px 5px;background:#c0392b;border:none;border-radius:3px;color:#fff;cursor:pointer;white-space:nowrap">Recover</button>
            <button class="slug-fixdup-btn" title="Видалити дублікат умов якщо вони збігаються з вимогами"
                style="font-size:10px;padding:2px 5px;background:#6a0dad;border:none;border-radius:3px;color:#fff;cursor:pointer;white-space:nowrap">Fix Dups</button>` : ''}
        `;

        const input = wrap.querySelector('.slug-input');
        const autoBtn = wrap.querySelector('.slug-auto-btn');
        const saveBtn = wrap.querySelector('.slug-save-btn');
        const recoverBtn = wrap.querySelector('.slug-recover-btn');
        const fixDupBtn = wrap.querySelector('.slug-fixdup-btn');

        autoBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const titleEl = blockElement.querySelector('h2, h3, h4, .news-feed-title, .vacancy-name');
            if (titleEl) input.value = this._toSlug(titleEl.textContent.trim());
        });

        saveBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const newSlug = input.value.trim();
            if (!newSlug || newSlug === currentSlug) return;
            try {
                saveBtn.textContent = '...';
                const result = await apiPut(apiPath, { slug: newSlug });
                if (result?.slug) {
                    blockElement.setAttribute(`data-${type}-slug`, result.slug);
                    input.value = result.slug;
                    const link = blockElement.querySelector('a.news-link-to-feed-container, a.active');
                    if (link && link.href) {
                        link.href = `/vacancy/${result.slug}`;
                    }
                    saveBtn.textContent = '✓';
                    setTimeout(() => { saveBtn.textContent = 'Save'; }, 1500);
                    showSuccessNotification(`Slug оновлено: ${result.slug}`);
                }
            } catch (err) {
                saveBtn.textContent = 'Save';
                showErrorNotification('Помилка збереження slug');
            }
        });

        if (recoverBtn) {
            recoverBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm('Відновити контент з оригінального імені блоку? Це перенесе контентні записи з оригінального slug-а до поточного.')) return;
                try {
                    recoverBtn.textContent = '...';
                    const result = await apiPost(`/api/vacancies/${entityId}/recover-content`);
                    recoverBtn.textContent = `✓ ${result.migrated}`;
                    showSuccessNotification(result.message || `Відновлено ${result.migrated} записів`);
                    setTimeout(() => { recoverBtn.textContent = 'Recover'; }, 3000);
                } catch (err) {
                    recoverBtn.textContent = 'Recover';
                    showErrorNotification('Помилка відновлення контенту');
                }
            });
        }

        if (fixDupBtn) {
            fixDupBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                try {
                    fixDupBtn.textContent = '...';
                    const result = await apiPost(`/api/vacancies/${entityId}/fix-duplicate-conditions`);
                    if (result.deleted) {
                        fixDupBtn.textContent = '✓';
                        showSuccessNotification(result.message);
                    } else {
                        fixDupBtn.textContent = '—';
                        showSuccessNotification(result.message);
                    }
                    setTimeout(() => { fixDupBtn.textContent = 'Fix Dups'; }, 3000);
                } catch (err) {
                    fixDupBtn.textContent = 'Fix Dups';
                    showErrorNotification('Помилка видалення дублікату');
                }
            });
        }

        wrap.addEventListener('click', e => e.stopPropagation());
        input.addEventListener('keydown', e => e.stopPropagation());

        const controls = blockElement.querySelector('.vacancy-status-controls');
        if (controls) controls.after(wrap);
        else blockElement.appendChild(wrap);
    }
}

export const vacancyManager = new VacancyManager();

// ВАЖНО: Делаем vacancyManager доступным глобально
window.vacancyManager = vacancyManager;

console.log('VacancyManager initialized and made globally available');